import { useSelector, useDispatch } from "react-redux";
import {
    DataGrid,
    gridClasses,
    GridToolbar,
} from '@mui/x-data-grid';

import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Checkbox,
}
    from "@mui/material";
import { styled } from '@mui/material/styles';
import React, {useEffect, useState, useMemo} from "react";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import {deleteCollection, getCollectionById, getCollections, scanComplete} from "./actions/collections";
import { setCollections } from "./reducers/collections";
import {getCustomers} from "./actions/customers";
import {setCustomers} from "./reducers/customers";
import {getAssetTypes} from "./actions/asset_types";
import {setAssetTypes} from "./reducers/asset_types";
import {useParams, useNavigate} from "react-router-dom";
import {setAssets, addAsset, updateAsset, deleteAsset, clearAssets} from "./reducers/assets";
import {createAsset, deleteAssetFromDb, editAssetOnDb,getSerialNumbers} from "./actions/assets";
import jwt_decode from "jwt-decode";
import {useCookies} from "react-cookie";
//import { useCSVReader } from 'react-papaparse';
//import useMediaQuery from '@mui/material/useMediaQuery';

import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from '@mui/material/Autocomplete';
import { EMPTY_STATS, statsFromCollection, statsByTypeName, applyAssetTransition } from "./utilities/stats";
import { modeOf, isBlind, showsStatTile, removeDeletesRow, wasAddedOnSite } from "./utilities/scanMode";
import { formatCityStateZip } from "./utilities/string";
import { buildScanIdentity, nextLocalScanSeq } from "./utilities/scanUid";
import { displayItemNumbers } from "./utilities/itemNumber";
import { SCAN_OUTCOME, classifyScan, serialKey } from "./utilities/scanMatch";
import {
    EMPTY_TALLY, recordDuplicate, duplicateCount, duplicateEvents, hiddenDuplicateCount,
    hasLiveDuplicateFor, clearDuplicates
} from "./utilities/duplicateTally";
import { playRejectTone } from "./utilities/sound";
import {
    shouldHandleScanKey, appendScanKey, isScanCharacter, isCompleteBarcode,
    emptyEntryEvidence, recordKeystroke, recordPaste, evidenceFor, classifyEntryMethod,
    describeEntryMethod
} from "./utilities/scanInput";
import { entryValuesFor, applyEntryValues } from "./utilities/entryValues";
import { canAddUnexpectedAsset } from "./utilities/permissions";

import { useRef } from 'react';

const pageSizeOptions = [20];

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
    [`&`]: { height: '850px' },
    [`& .${gridClasses.row}.even`]: {
        backgroundColor: '#F8F8F8'
    },
    [`& .${gridClasses.row}.legacy,& .${gridClasses.row}.collected`]: {
        backgroundColor: '#c9ffde'
    },
    // Spec §2.2: an unexpected item is highlighted yellow. Declared AFTER .collected and
    // with a doubled class selector so it wins -- every unexpected asset is also collected,
    // which is why these rows have always rendered green instead.
    [`& .${gridClasses.row}.unexpected.unexpected`]: {
        backgroundColor: '#fff3bf'
    },
    // P1-3: the already-collected row a duplicate scan hit. Cleared on the next accepted
    // entry, so it marks "this is the one you just tried to scan again" and nothing more.
    [`& .${gridClasses.row}.duplicate-flash.duplicate-flash`]: {
        backgroundColor: '#ffd3d3',
        boxShadow: 'inset 4px 0 0 0 #c62828'
    },
    [`& .${gridClasses.row}.Mui-selected`]: {
        backgroundColor: '#006225',
        color: 'white'
    },
    [`& .${gridClasses.row}.Mui-selected:hover`]: {
        backgroundColor: '#009f3c',
        color: 'white'
    },
}));


const AssetManagement = (props = {}) => {
    const dispatch = useDispatch();
    const { id } = useParams();
    // P1-13: Scan Complete hands off to the report page (spec §3).
    const navigate = useNavigate();
    const [ isLoaded, setIsLoaded ] = useState(false);

    //const theme = useTheme();
    //const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    //const { CSVReader } = useCSVReader();

    const collections = useSelector(state => state.collections.value);

    const customers = useSelector(state => state.customers.value);
    const assetTypes = useSelector(state => state.assetTypes.value);
    const assets = useSelector(state => state.assets.value);
    const auth = useSelector(state => state.auth.value);
    const states = useSelector(st => st.states.value );
    //const [cookies, setCookie, removeCookie] = useCookies(['access_token']);
    const [cookies] = useCookies(['access_token']);
    // P1-6: the signed-in user's role, read the same way the delete-icon and edit
    // gates below already read it. Hoisted rather than decoded a third time inside a
    // renderCell closure -- the token is the same for the life of the screen.
    //
    // Guarded because this now runs on every render rather than only when the grid
    // renders: App.js shows the login form until a token exists, so there is normally
    // one here, but a cookie cleared while the app is open would otherwise take the
    // whole screen down instead of one column. `null` fails closed -- no role means no
    // Manager+ controls -- which is the right direction for a gate.
    const userRole = (() => {
        try {
            return jwt_decode(cookies.access_token).user.role;
        } catch {
            return null;
        }
    })();
    const [ newAssetId, setNewAssetId ] = useState(null);
    const [ newAssetAssetType, setNewAssetAssetType ] = useState(null);
    const [ newAssetSerialNumber, setNewAssetSerialNumber ] = useState('');
    const [ newAssetAssetTag, setNewAssetAssetTag ] = useState('');
    const [ newAssetDescription, setNewAssetDescription ] = useState('');
    const [ newAssetNotes, setNewAssetNotes ] = useState('');
    const [ selectedCollection, setSelectedCollection ] = useState(null);
    const [ collectionsWithRelations, setCollectionsWithRelations ] = useState([]);
    const [ assetsWithRelations, setAssetsWithRelations ] = useState([]);


    const [ loading, setLoading ] = useState(false);
    const [ selectionModel, setSelectionModel] = useState([]);
    // P1-15: the asset type is sticky by DEFAULT -- scanning 141 laptops should not
    // mean 141 trips to the dropdown. The checkbox is rendered inverted, as an opt-in
    // "Clear after each scan".
    const [ pinAssetType, setPinAssetType] = useState(true);
    const [ pinDescription, setPinDescription] = useState(false);
    const [ pinNotes, setPinNotes] = useState(false);
    // The type the operator explicitly picked. Kept separate from newAssetAssetType
    // because a matched scan overwrites the latter with the matched row's own type;
    // without this the pinned value would drift to whatever was last scanned.
    const [ pinnedAssetType, setPinnedAssetType] = useState('');
    const [stats , setStats ] = useState(EMPTY_STATS);
    const [dataFilter , setDataFilter ] = useState([]);
    const [openSnackbar, setSnackbar ] = useState(null);
    const [confirm, setConfirm] = useState({'open':false});
    const [overrideId, setOverrideId] = useState(null);

    // P1-3 / P1-8. The duplicate tally is a SESSION counter, deliberately not part of
    // `stats`: the other four tiles describe rows that exist and come from one
    // server-side definition (P1-2), while a duplicate is a scan that was refused and
    // persisted nowhere. See utilities/duplicateTally.js.
    const [duplicateTally, setDuplicateTally] = useState(EMPTY_TALLY);
    // A refused scan gets a blocking notice rather than the snackbar: the snackbar
    // auto-hides after 6s and has a single slot, so a following message replaces it.
    // A duplicate the operator never saw is the 03/06/2026 symptom all over again.
    const [blockingNotice, setBlockingNotice] = useState(null);
    const [showDuplicateLog, setShowDuplicateLog] = useState(false);
    // P1-5: the per-type breakdown spec §4.3 asks for. Behind a click on the Total
    // Scanned tile rather than fourteen tiles of its own -- five already overflowed a
    // 1366px driver laptop (P1-8). Same pattern as the duplicate log above.
    const [showTypeBreakdown, setShowTypeBreakdown] = useState(false);
    // The row a duplicate scan collided with, highlighted until the next accepted entry.
    const [flashDuplicateId, setFlashDuplicateId] = useState(null);

    const handleSnackbarClose = () =>{
        setSnackbar(null);
    }

    const handleConfirmClose =()=>{
        let params = {'open':false}
        setConfirm(params)
    }

    const scanCompleteAction = async(collection)=>{
        handleConfirmClose();
        const result = await scanComplete(auth.token, collection);
        if(result.status ==='success'){
            const updatedCollections = await getCollections(auth.token);
            dispatch(setCollections(updatedCollections.data));

            setSnackbar({status:'success','message':result.message});

            // P1-13 / spec §3: "after confirmation, the software must generate the
            // report." This used to end here, at a flag flip -- the report lived behind
            // an unrelated Export XLS button on another screen that nothing pointed the
            // driver at.
            //
            // The offer is a DIALOG, not the snackbar above: the snackbar
            // autoHideDuration is 6s, so a handoff attached to it would vanish while the
            // driver was still reading it.
            //
            // Declining is a real option and must not undo anything -- scanning is
            // already complete either way, and the report stays available from the
            // reports page. That is why the report is offered here rather than
            // downloaded outright.
            setConfirm({
                'open': true,
                'title': 'Scanning Complete',
                'msg': 'Generate the report for this order now?',
                'btn_msg': 'View Report',
                'cancel_msg': 'Not Now',
                'action': () => {
                    handleConfirmClose();
                    // ?export=1 makes the report page run its own export once its stats
                    // have loaded. Navigating rather than exporting from here is
                    // deliberate: downloadCsv is ~370 lines welded to CollectionReports'
                    // state, and duplicating or extracting it to satisfy a wiring
                    // requirement would put the riskiest code in the app at risk.
                    navigate(`/CollectionReports/${collection.id}?export=1`);
                }
            });
        }else{
            setSnackbar({status:'error','message':'Oh Snap! Something went wrong. Please try again'});
        }
    }

    // "Remove From Collection" means two different things, and which one depends on where the row
    // came from. See utilities/scanMode.js -> wasAddedOnSite for the rule.
    //
    // An INTENT-LIST row exists whether or not anyone scans it, so removing it un-collects it: back
    // to Unscanned, still on the list, still expected. Unchanged.
    //
    // A row that exists ONLY because somebody scanned it -- everything in a blind order, and every
    // `unexpected` row in an interactive one -- has nowhere to go back to. Un-collecting it left a
    // row representing nothing: CompuCycle reported it against blind mode on 2026-08-21 (the count
    // dropped, the row stayed), and in an interactive order it was worse, because an uncollected
    // unexpected row still counts in the Unexpected tile while matching none of the report's three
    // body sections -- so it vanished from the export while remaining on screen. Removing it now
    // deletes it.
    //
    // ⚠ The button label stays "Remove From Collection" in both cases (client's call), so THIS
    // DIALOG is the only place the operator is told the difference. Its wording is load-bearing.
    const uncollectAssetConfirm = () => {
        const asset = assets.find(a => a.id === newAssetId);

        if (removeDeletesRow(asset, modeOf(selectedCollection))) {
            setConfirm({
                'open': true,
                'action': () => deleteTheAsset(asset.id, asset.collection_id),
                'title': 'Confirm Remove',
                'msg': 'This item was added by scanning, so removing it will delete it from the '
                    + 'list entirely. This cannot be undone.',
                'btn_msg': 'Yes! Delete It'
            });
            return;
        }

        setConfirm({'open':true,
            'action':uncollectAsset,
            'title':'Confirm Remove',
            'msg':'Are you sure you want to remove this item from the collected assets? It stays '
                + 'on the intent list and returns to Unscanned.',
            'btn_msg':'Yes! Remove It'})
    }

    const deleteTheAsset = async(id, collection_id) => {
        setConfirm({open:false});
        const result = await deleteAssetFromDb(auth.token, id);
        if( result.status === "success") {
            dispatch(deleteAsset(id));
            const collection = await getCollectionById(collection_id, auth.token);
            setStats(statsFromCollection(collection));
            // Added 2026-08-21. This path cleared nothing, so after a delete the editor still held
            // the deleted row's serial and `newAssetId` still pointed at a row that no longer
            // exists -- which then decided the Collect button's label. `uncollectAsset` has always
            // cleared, and the two removal routes should not differ. Also puts the cursor back in
            // Serial # for the next scan.
            completeEntry();
            setSnackbar({status:'success','message':'Well Done! Asset successfully deleted.'});
        } else {
            setSnackbar({status:'error','message':'Oh Snap! Unable to delete asset.'});
        }


        //const canDeletes = (selectedCollection != null && Date.parse(selectedCollection.start_time) > (Date.now() - 1000 * 60 * 60 * 24 * 7));
        // can delete if they are a super admin / admin / manager or if the collection is less than a week old
        /*
        const canDelete = [1,2,3,4,5,6].find(x => jwt_decode(cookies.access_token).user.role === x) || (selectedCollection != null && Date.parse(selectedCollection.start_time) > (Date.now() - 1000 * 60 * 60 * 24 * 7));
        disabled={!canDelete} ->ilagay mo ito sa button sa baba
         */

        //console.log(confirm)
    }


    const uncollectAsset = async() => {
        setConfirm({'open':false})
        let asset = assets.find(a => newAssetSerialNumber !== "" && a.serial_number === newAssetSerialNumber);
        if( !asset ){
            setSnackbar({status:'error','message':'Collection Failed. Asset not found'});
            return;
        }
        const result = await editAssetOnDb(auth.token, Object.assign({}, asset, { collected: false }));
        if (!result || result.status !== 'success') {
            setSnackbar({status: 'error', 'message': result?.message || 'Oh Snap! Unable to remove asset from collected assets.'});
            return;
        }
        dispatch(updateAsset({ ...asset, ...(result.data || {}), collected: false }));

        const collection = await getCollectionById(asset.collection_id, auth.token);
        setStats(statsFromCollection(collection));
        completeEntry();
        setSnackbar({status:'success','message':'Well Done! Asset successfully removed from collected assets!'});
    }

    // ── P1-3: one entry point for every scan, from every source ──────────────────
    //
    // Replaces `collectAsset`, which re-derived the match itself with raw `===` (so it
    // could disagree with the normalised comparison the filter effect had just done),
    // and then checked for a duplicate by looking up `newAssetId` -- form state written
    // by an effect -- rather than the row it had resolved two lines earlier. All of that
    // is now one pure call: see utilities/scanMatch.js.
    //
    // The gun, the typed Enter and the Collect button all arrive here, so they cannot
    // behave differently from one another. That is the requirement's "identical in every
    // browser" met by construction, rather than by keeping three code paths in step.
    // P1-18: `source` (a hardcoded 'gun'/'typed' per code path) is replaced by `entry` — how the
    // characters actually arrived (`method`), the measurements that verdict came from (`evidence`),
    // and which handler fired (`trigger`). The first two are the honest answer; `trigger` is kept
    // only because it is occasionally useful when diagnosing, and is not shown to the operator.
    const runScanEntry = async ({ serial, assetTag, method, evidence, trigger }) => {
        const entry = { method, evidence, trigger };
        const collection = selectedCollectionRef.current;

        if (collection == null) {
            refuseEntry({
                title: 'No Order Selected',
                message: 'Choose an order before scanning.'
            });
            return;
        }

        // ⚠ Added 08/11/2026. Opening a collection sets loading, awaits the asset list, then
        // dispatches it. Nothing used to stop a scan landing inside that window, and a scan
        // classified against the PREVIOUS order's rows (or an empty list) comes out UNEXPECTED --
        // so it was refused as "Asset Type Required", or the server refused it as
        // SERIAL_ON_ORDER, and clicking Collect a moment later worked. That is one of the
        // "sometimes it doesn't collect automatically" cases, and it is most likely on the FIRST
        // scan after picking an order, which is exactly when a driver starts.
        if (loadingAssetsRef.current) {
            refuseEntry({
                title: 'Still Loading',
                message: 'The order\'s item list is still loading. Please scan again in a moment.'
            });
            return;
        }

        // The gun listener is on the document unconditionally, so scanning into a
        // finished order used to create rows even though the buttons were hidden.
        if (collection.scan_complete === true) {
            refuseEntry({
                title: 'Scanning Complete',
                message: 'This order is marked scan complete, so no further items can be added.'
            });
            return;
        }

        const decision = classifyScan({
            // Both scan routes now pass the tag explicitly (empty), so a tag left in the form
            // from a previous item can no longer win the match. It used to be read from the
            // form here, and scanMatch's tag tier beats a zero-serial match -- so scanning an
            // unlisted item while row A's tag lingered resolved to row A: either collecting the
            // wrong asset, or reporting a bogus duplicate.
            serial,
            assetTag: assetTag !== undefined ? assetTag : '',
            assets: assetsRef.current,
            collectionId: collection.id
        });

        // Decided once, synchronously, and passed down -- so nothing downstream re-reads form
        // state that may have been written in this same tick, and a scan of a different item
        // cannot inherit the previous item's notes or tag. See utilities/entryValues.js.
        const values = entryValuesFor({
            assetId: decision.assetId,
            form: formRef.current,
            pins: { pinAssetType, pinnedAssetType, pinDescription, pinNotes }
        });

        switch (decision.outcome) {
            case SCAN_OUTCOME.DUPLICATE:
                registerDuplicateRejection(decision, entry);
                return;

            case SCAN_OUTCOME.MATCH:
                await collectMatchedAsset(decision, values, entry);
                return;

            case SCAN_OUTCOME.UNEXPECTED:
                // The scanned serial is passed explicitly -- see saveNewAsset's note on
                // why reading it from state here would be one entry behind.
                await saveNewAsset(true, decision, entry, serial, values);
                return;

            case SCAN_OUTCOME.AMBIGUOUS:
                // Never guess between candidates. Show them, so the operator can see what
                // the conflict actually is instead of being told to fix something invisible.
                setDataFilter({ hasFilter: true, filterType: 'ids', value: decision.candidateIds });
                refuseEntry({ title: 'More Than One Match', message: decision.message });
                return;

            case SCAN_OUTCOME.EMPTY:
            default:
                refuseEntry({ title: 'Nothing To Scan', message: decision.message });
                return;
        }
    };

    // A matched intent-list row: collect it. The body is built from the DECISION plus the values
    // runScanEntry already decided, never from state that may have been written in this tick.
    const collectMatchedAsset = async (decision, values, entry) => {
        const asset = decision.asset;

        const newAsset = {
            id: asset.id,
            "collection_id": selectedCollectionRef.current?.id ?? selectedCollectionId,
            "collector_id": auth.id,
            ...applyEntryValues(asset, values),
            "serial_number": asset.serial_number,
            "pending": true,
            "collected": true,
            "legacy": false,
            "make_id": asset.make_id,
            "manufacturer": asset.manufacturer,
            "model": asset.model,
            "model_id": asset.model_id,
            // P1-12: identify this scan on the device. The server records its own
            // received-at alongside it, stamps collected_timestamp, and assigns the
            // authoritative scan_number.
            ...takeScanIdentity()
        };

        const result = await editAssetOnDb(auth.token, newAsset);

        // The server refused it as a duplicate, meaning it knew something this device's
        // asset list did not -- another device collected the item first. Counted the same
        // way as a locally detected duplicate, or the two-device case stays invisible.
        if (result?.code === 'DUPLICATE_SCAN') {
            registerDuplicateRejection(
                { ...decision, duplicateOfId: result.duplicateOfId ?? asset.id },
                entry,
                { fromServer: true });
            return;
        }

        if (!result || result.status !== 'success') {
            refuseEntry({
                title: 'Collection Failed',
                message: result?.message || 'Oh Snap! Collection failed. Please try again.'
            });
            return;
        }

        const saved = {...asset, ...newAsset, ...(result.data || {})};
        dispatch(updateAsset(saved));

        // ⚠ CHANGED 08/11/2026. This used to `await getCollectionById(...)` here -- a SECOND
        // round trip that downloads the whole order (every asset, two joins) purely to refresh
        // the five tiles. The re-entrancy guard is held across both calls, so every scan arriving
        // during that window was discarded with only a 6-second toast. At any real scanning pace
        // on a warehouse connection that is routine, and it presents exactly as Christian
        // described: the scan didn't collect, clicking Collect did.
        //
        // The server already returns the saved row, so the tiles are recomputed locally from the
        // list we just updated. `statsAfterCollect` mirrors CC_Backend/utils/stats.js; the next
        // server payload (an import, a delete, reopening the order) still overwrites it, so this
        // is a local fast-path and not a second definition of the counts.
        setStats(prior => applyAssetTransition(prior, asset, saved));
        completeEntry();
        setDataFilter({top: asset.id});
        setSnackbar({status: 'success', 'message': 'Well Done! Asset Collection Successful'});
    }

    // P1-8: the single place the Duplicate count moves. Exactly one increment per
    // rejected attempt -- a locally classified duplicate never reaches the network, and a
    // server-detected one never passed the classifier, so they cannot both fire.
    const registerDuplicateRejection = (decision, entry = {}, options = {}) => {
        const collectionId = selectedCollectionRef.current?.id ?? selectedCollectionId;

        setDuplicateTally(tally => recordDuplicate(tally, {
            collectionId,
            serial: decision.serialKey,
            assetTag: decision.tagKey,
            // P1-18: the row this refusal was ABOUT. The count now depends on it — an event stops
            // counting once its row is deleted or un-collected, because at that point a re-scan
            // would succeed and the warning is stale.
            assetId: decision.duplicateOfId,
            at: new Date().toISOString(),
            method: entry.method,
            trigger: entry.trigger,
            evidence: entry.evidence
        }));

        if (decision.duplicateOfId != null) {
            // Float the row it collided with to the top and flash it, so "already
            // collected" is a thing the operator can see rather than just be told.
            setFlashDuplicateId(decision.duplicateOfId);
            setDataFilter({top: decision.duplicateOfId});
        }

        refuseEntry({
            title: 'Duplicate Scan',
            message: decision.message || 'Duplicate Scan — this item is already collected on this order.',
            detail: options.fromServer
                ? 'Another device collected this item first.'
                : null
        });

        if (options.fromServer) {
            // Our asset list was behind. Catch it up so the tiles and row colours agree
            // with what the server just told us.
            refreshCollection();
        }
    };

    const refreshCollection = async () => {
        const collectionId = selectedCollectionRef.current?.id;
        if (collectionId == null) {
            return;
        }
        const collection = await getCollectionById(collectionId, auth.token);
        if (collection?.data?.assets != null) {
            dispatch(setAssets(collection.data.assets));
            setStats(statsFromCollection(collection));
        }
    };


    const handleSelection = (newSelection) => {
        if (newSelection) {
            if( ( newSelection.length === 1 && selectionModel.length === 1 ) && newSelection[0] === selectionModel[0]) {
                emptyNewAssetFields();
            } else {
                const selectedAsset = assets.find(a => a.id === newSelection[0]);
                if(selectedAsset){
                    setAssetEditorFields(selectedAsset.id, selectedAsset.asset_type_id, selectedAsset.serial_number, selectedAsset.asset_tag, selectedAsset.description, selectedAsset.collection_notes);
                    setSelectionModel(newSelection);
                }
            }
        } else {
            setSelectionModel([]);
        }
    }

    const handleDataFilter = (props) => {
        emptyNewAssetFields();
        setDataFilter({...props})
    }

    const handleNewAssetAssetTypeSelected = (value) => {
        setNewAssetAssetType(value);
        // P1-15: remember the operator's explicit choice so it survives resets.
        setPinnedAssetType(value);
        setDataFilter({hasFilter: true, filterType: 'asset', 'value': value})
    }

    const handleNewAssetSerialNumber = (value) => {
        setNewAssetSerialNumber(value)
        setDataFilter({hasFilter: true, filterType: 'serial', 'value': value})
    }


    const handleNewAssetTag = (value) => {
        setNewAssetAssetTag(value);
        setDataFilter({hasFilter: true, filterType: 'tag', 'value': value})
    }

    const handleSerialTyped = (value) => {
        setNewAssetAssetType(value);
        setDataFilter({hasFilter: true, filterType: 'asset', 'value': value})
    }

    // P1-12: the same Item # the exported report shows, from the same helper, so the
    // operator's screen and the customer's report can never disagree about it.
    //
    // Computed from the full `assets` slice, NOT from assetsWithRelations, which is the
    // tile-filtered subset -- deriving the fallback from a filtered list would make the
    // numbers shift every time someone clicked a stat tile.
    //
    // Rows imported before this feature shipped have no stored item_number and are not
    // backfilled (see migration 002). They get a stable display index assigned after the
    // highest real Item #, which is a display position, not an intent-list position.
    const itemNumbers = useMemo(() => displayItemNumbers(assets), [assets]);

    // Which row, if any, should currently be flashed as "this is the one you just tried to scan
    // again". DERIVED rather than tracked, which is the point.
    //
    // `flashDuplicateId` alone was set on refusal and unset only by the next ACCEPTED entry, so it
    // outlived its own cause: CompuCycle pressed Clear on the duplicate log, watched the count go
    // to zero, and the row stayed pink until they scanned something else (reported 2026-08-21).
    // That is the same defect P1-18 fixed for the count on this very tile, so it gets the same
    // treatment -- the highlight lasts exactly as long as a standing refusal against that row.
    //
    // Falls out for free: deleting the row, or un-collecting it, also ends the highlight, because
    // after either a re-scan would succeed.
    //
    // Computed once per render, NOT inside getRowClassName, which runs per row.
    const activeFlashId = useMemo(
        () => (hasLiveDuplicateFor(duplicateTally, selectedCollection?.id, assets, flashDuplicateId)
            ? flashDuplicateId
            : null),
        [duplicateTally, selectedCollection?.id, assets, flashDuplicateId]);

    const columns = [
        {
            field: "edit-action",
            width: 20,
            headerName: "",
            sortable: false,
            renderCell: (params) => {
                const onClick = (e) => {
                    setAssetEditorFields(params.row.id, params.row.asset_type_id, params.row.serial_number, params.row.asset_tag, params.row.description, params.row.notes);
                };

                const canEdit = [1,2,3].find(x => jwt_decode(cookies.access_token).user.role === x) || (selectedCollection != null && Date.parse(selectedCollection.start_time) > (Date.now() - 1000 * 60 * 60 * 24 * 7));

                return <IconButton variant="outlined" onClick={onClick} disabled={!canEdit}>
                    <EditIcon/>
                </IconButton>
            }
        },
        { field: 'id', headerName: 'ID', width: 100, editable: false},
        // P1-12: Item # is the row's position in the intent list, not the database id.
        { field: 'item_number', headerName: 'Item #', width: 90, editable: false, type: 'number',
            valueGetter: (params) => itemNumbers.get(params.row.id) ?? null,
            renderCell: (params) => params.value == null ? '—' : params.value },
        { field: 'scan_uid', headerName: 'Scan UID', width: 240, editable: false },
        { field: 'asset_type', headerName: 'Asset Type', width: 200, editable: false, valueGetter: (params) => params.row.assetType != null ? params.row.assetType.name : ""},
        { field: 'manufacturer', headerName: 'Make', width:150 },
        { field: 'model', headerName: 'Model', width: 300 },
        { field: 'serial_number', headerName: 'Serial Number', width: 150, editable: false},
        { field: 'asset_tag', headerName: 'Asset Tag', width: 125, editable: false },
        { field: 'description', headerName: 'Description', width:125, editable: false },
        { field: 'collection_notes', headerName: 'Notes', width: 300, editable: false },



        { field: 'comments', headerName: 'Comments', width: 300, editable: false },
        /*
        {
            field: "collected",
            headerName: "Collected",

            sortable: false,
            renderCell: (params) => {
                return true;
                if( params.row.collected || params.row.legacy ) {
                    return "True"
                } else {
                    return "False"
                }
            }
        },
*/

        {
            field: "delete-action",
            headerName: "",
            width: 50,
            sortable: false,
            renderCell: (params) => {
                const onClick = async (e) => {
                    e.stopPropagation();
                    let pars = {open: true,
                        title:'Confirm Delete',
                        msg:'Are you sure you want to permanently delete this asset? This action cannot be undone.',
                        btn_msg:'Yes! Do it',
                        action: () => deleteTheAsset(params.row.id, params.row.collection_id)
                    };
                    setConfirm(pars)
                };
                //const canDelete = [1,2,3].find(x => jwt_decode(cookies.access_token).user.role === x);
                // P1-6: `userRole` is now decoded once at the top of the component rather
                // than again here, so this closure reads the same value the button gate does.
                // Manager+ may delete anything; below that, you may delete a row you put there
                // yourself. `unexpected === true` used to stand in for "added on site", and P1-5
                // broke that proxy: a blind row is `unexpected = false` BY DEFINITION, so every
                // row in a blind order read as an intent-list row and a Collector saw no delete
                // icon at all -- leaving the Remove button as their only way out of a mis-scan.
                // Asking the real question instead, from the same rule that decides whether
                // removing a row deletes it.
                const canDelete = [1, 2, 3].includes(userRole)
                    || wasAddedOnSite(params.row, modeOf(selectedCollection));
                if(canDelete) {
                    return <IconButton variant="outlined" onClick={onClick}>
                        <DeleteIcon/>
                    </IconButton>
                }

            }
        },
    ]



    useEffect(() => {
        const fetchData = () => async (dispatch) => {
            dispatch(clearAssets());
            if (customers.length === 0) {
                const updatedCustomers = await getCustomers(auth.token);
                dispatch(setCustomers(updatedCustomers.data));
            }
            if (assetTypes.length === 0) {
                const updatedAssetTypes = await getAssetTypes(auth.token);
                dispatch(setAssetTypes(updatedAssetTypes.data));
            }
            if (collections.length === 0) {
                const updatedCollections = await getCollections(auth.token);
                dispatch(setCollections(updatedCollections.data));
            }
            setIsLoaded(true);
        }
        dispatch(fetchData());
    }, [dispatch,assetTypes,auth,collections,customers] );


    useEffect(() => {
        setCollectionsWithRelations(isLoaded ? collections
            .map( collection => {
                //wag kana mangealam dito
                const cust = customers.find( customer => customer.id === collection.customer_id);
                return Object.assign({},
                    collection,
                    {
                        customer: cust || { } ,
                        // cust is optional above, so it has to be optional here too.
                        state: states.find(s => s.id === cust?.state_id)
                    } );
            })
            .sort((a,b) => {
                if (a.customer.customer_name !== b.customer.customer_name) {
                    // customer_name is missing whenever cust fell back to {} above.
                    return (a.customer.customer_name || '').localeCompare(b.customer.customer_name || '')
                }
                if (a.lot_number !== b.lot_number) {
                    return parseInt(a.lot_number,10) - parseInt(b.lot_number, 10);
                }
                return 0;
            }) : []);

    }, [collections, isLoaded,customers,states]);

    // Filter assetsWithRelations — WHICH ROWS TO SHOW, and nothing else.
    //
    // ⚠ This effect must not touch editor state. It has twice been the cause of a reported
    // defect by doing so, and the rule is now absolute: it may compute `filteredAssets` and
    // highlight a row, and nothing more.
    //
    // 1. P1-3: it used to DECIDE things. Its serial branch called guardedCollect() as a side
    //    effect, but only when `dataFilter.entered === 1` -- the typed-Enter flag. A gun scan set
    //    `collect:1` and never `entered:1`, so a gun scan matching zero rows, or two, fell
    //    through the else and silently cleared four form fields: no save, no message, nothing.
    //    Deciding moved to runScanEntry, synchronously, from a pure classifier.
    //
    // 2. 08/11/2026, from Christian's staging test: it still called setAssetEditorFields(), so it
    //    wrote the matched row's asset_type_id over the operator's dropdown selection. Because
    //    only the TYPED path sets a serial filter (the gun calls runScanEntry directly), the
    //    dropdown survived a gun scan but was overwritten-then-reset on a typed one -- and since
    //    a refusal focuses the Serial # field, which route ran depended on whether the previous
    //    entry was refused. That is the "sometimes it keeps the asset type, sometimes it clears
    //    it" report. An entry's values are now decided once, explicitly, in
    //    utilities/entryValues.js.
    useEffect(() => {
        const enrichedAssets = isLoaded ? assets
            .map(asset => Object.assign({}, asset, {
                collection: collectionsWithRelations.find(collection => collection.id === asset.collection_id),
                assetType: assetTypes.find(assetType => assetType.id === asset.asset_type_id),
            })) : [];
        let filteredAssets = enrichedAssets;
        if(dataFilter.hasFilter){
            if(dataFilter.filterType ==='stat'){
                // P1-2: these must mirror CC_Backend/utils/stats.js exactly, or a
                // tile's number won't match the rows you get when you click it.
                // collected/unexpected are nullable, so never compare to false.
                if(dataFilter.value === 'unexpected'){
                    filteredAssets = enrichedAssets.filter(asset => asset.unexpected === true);
                }else if(dataFilter.value ==='totalScanned'){
                    // P1-5: blind mode's one row-count tile. Mirrors totalScanned in
                    // CC_Backend/utils/stats.js — everything collected, matched and
                    // unexpected alike — so the number and the rows behind it agree.
                    filteredAssets = enrichedAssets.filter(asset => asset.collected === true);
                }else if(dataFilter.value ==='unscanned'){
                    filteredAssets = enrichedAssets.filter(asset => asset.unexpected !== true && asset.collected !== true);
                }else if(dataFilter.value ==='matched'){
                    filteredAssets = enrichedAssets.filter(asset => asset.unexpected !== true && asset.collected === true);
                }else{
                    filteredAssets = enrichedAssets.filter(asset => asset.unexpected !== true);
                }
            }else if(dataFilter.filterType ==='asset'){
                filteredAssets = enrichedAssets.filter(asset => asset.asset_type_id === dataFilter.value);
            }else if(dataFilter.filterType ==='ids'){
                // P1-3: show exactly these rows. Used when a scan was AMBIGUOUS, so the
                // operator can see the conflicting rows rather than being told to fix
                // something they cannot see.
                const ids = Array.isArray(dataFilter.value) ? dataFilter.value : [];
                filteredAssets = enrichedAssets.filter(asset => ids.includes(asset.id));
            }else if(dataFilter.filterType ==='tag'){
                var tag = dataFilter.value;
                filteredAssets = enrichedAssets.filter(asset => asset.asset_tag?.includes(tag));

                // Highlight the row so the operator can see what the filter landed on. Setting
                // the selection model does NOT run handleSelection (that fires on user
                // interaction only), so this cannot write to the editor.
                if(filteredAssets.length===1 && filteredAssets[0].asset_tag===tag ){
                    setSelectionModel([filteredAssets[0].id]);
                }
            }else if(dataFilter.filterType ==='serial'){
                var serial = dataFilter.value;
                // Through serialKey, so the grid narrows on exactly the identity the
                // classifier matched on. Two different normalisations here and there is how
                // the old code could show a row it then refused to collect.
                const wanted = serialKey(serial);
                filteredAssets = enrichedAssets.filter(asset => serialKey(asset.serial_number) === wanted);

                setSelectionModel(filteredAssets.length === 1 ? [filteredAssets[0].id] : []);
            }
        }
        setAssetsWithRelations(filteredAssets);
        // `newAssetAssetType` is deliberately NOT a dependency any more: this effect no longer
        // reads or writes any editor field, so a type change cannot affect what it computes.
    }, [assets, assetTypes, collectionsWithRelations, isLoaded, dataFilter]);

    useEffect(() => {
        const the_id = overrideId !== null ? parseInt(overrideId) : id ? parseInt(id) : null;
        if (the_id !== null) {
            setSelectedCollection(collectionsWithRelations.find( collection => collection.id === the_id))
        }
        /*
        if( id != null && id !== '' ) {
            setSelectedCollection(collectionsWithRelations.find( collection => collection.id === parseInt(id)))
        }
         */
        console.log('effect 575')

    }, [ collectionsWithRelations, isLoaded,id]);


    // P1-12: the device's own view of the scan sequence, used only to fill the
    // trailing segment of the scan uid. The authoritative scan_number is assigned by
    // the server, which is the only party that can see two devices on one order.
    // Re-seeded whenever a collection is opened so a fresh device continues the
    // sequence rather than restarting it at 1.
    const scanSeq = useRef(1);

    useEffect(() => {
        setLoading(true);
        selectedCollectionRef.current = selectedCollection;
        // Set synchronously, not via state: a scan fired in the next millisecond has to see it.
        // Until this clears, `assetsRef` still holds the PREVIOUS order's rows, so classifying
        // against it would call everything unexpected.
        loadingAssetsRef.current = selectedCollection != null;
        const fetchData = async() => {
            if (selectedCollection != null) {
                const collection = await getCollectionById(selectedCollection.id, auth.token);
                dispatch(setAssets(collection.data.assets));
                setStats(statsFromCollection(collection));
                scanSeq.current = nextLocalScanSeq(collection.data.assets);
                // The dispatch above is what makes assetsRef correct, and it is applied before
                // this line runs, so releasing the guard here cannot expose the stale list.
                assetsRef.current = collection.data.assets ?? [];
            }
            loadingAssetsRef.current = false;
            setLoading(false);
            // Ready to scan. Until this, focus was wherever the operator last clicked -- most
            // often still inside the order Autocomplete, which swallows the barcode and ignores
            // its Enter, so the first scan of a session could disappear without a trace.
            if (selectedCollection != null) {
                focusSerialInput();
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ selectedCollection,auth,dispatch]);

    // P1-8: the duplicate tally belongs to one order. Opening a different collection
    // starts a fresh one rather than carrying a stale number across -- the tally itself
    // enforces this (it stores its own collection id), so this only clears the UI that
    // hangs off it.
    useEffect(() => {
        setDuplicateTally(EMPTY_TALLY);
        setFlashDuplicateId(null);
        setShowDuplicateLog(false);
    }, [selectedCollection?.id]);

    // Mint the identity for the scan about to be sent. The lot number is the order
    // number the client knows the job by, so it is what goes in the uid.
    const takeScanIdentity = () => {
        const identity = buildScanIdentity(selectedCollection?.lot_number, scanSeq.current);
        scanSeq.current += 1;
        return identity;
    };




    // ── P1-3: the scan buffer, and the actual cause of the Edge/Chrome divergence ──
    //
    // These were `useState` (scanBuffer, lastKeyTime, scannedData) and the listener was
    // re-registered on [scanBuffer, lastKeyTime]. Under React 18's automatic batching --
    // which applies to native event handlers too -- the values read inside the handler
    // are whatever was last COMMITTED, not what the previous keystroke just set. So
    // within one burst from the gun:
    //
    //   * `lastKeyTime` was still the pre-burst value for every keystroke React hadn't
    //     committed, so `timeSinceLastKey > 100` kept coming out true and the buffer was
    //     RESET on each character instead of appended to;
    //   * `scanBuffer.length` on Enter was a stale read, so a complete scan could look
    //     short and fall into the "Barcode too short" branch -- which was never
    //     rendered, so it vanished silently.
    //
    // Whether a commit lands between two HID keystrokes is scheduler- and
    // browser-dependent. That is what "the counts weren't working in Edge" was: not a
    // count bug at all, a truncated or discarded serial. Refs make the accumulation
    // synchronous, so it cannot depend on render timing, and the listener is registered
    // once. Nothing renders the buffer, so state bought nothing and cost determinism.
    const scanBufferRef = useRef('');
    const lastKeyTimeRef = useRef(0);

    // ── Latest-value refs ────────────────────────────────────────────────────────
    // The scan decision and the request body it produces are built in ONE synchronous
    // pass, from these, rather than from state that may have been set in the same tick.
    //
    // That ordering discipline is the general form of two bugs already fixed by hand
    // here: `collectAsset` reading `newAssetId` (form state written by an effect) instead
    // of the row it had just resolved, and P1-17's clear-then-immediately-refilled form.
    // Reading through a ref makes "the value as of now" unambiguous.
    const assetsRef = useRef(assets);
    const selectedCollectionRef = useRef(null);
    const formRef = useRef({});
    // Declared here rather than next to rejectEntry because the document scan listener needs it
    // to decide whether a keystroke belongs to the scanner or to the Serial # field.
    const serialInputRef = useRef(null);
    // True while a collection's assets are being fetched. A scan during that window would be
    // classified against the PREVIOUS order's rows (or none at all) and come out "unexpected".
    const loadingAssetsRef = useRef(false);
    // The listener below is registered once, so it must not close over the first
    // render's callback. It calls through this instead.
    const runScanEntryRef = useRef(() => {});

    // P1-18: how the characters of the CURRENT entry arrived — keystroke count, first/last
    // timestamps, and whether a paste event fired. Both entry routes feed this, so the answer no
    // longer depends on which code path ran (see scanInput.js). Reset by finishEntryEvidence()
    // after every entry, so one entry's evidence cannot describe the next.
    const entryEvidenceRef = useRef(emptyEntryEvidence());

    const noteKeystroke = () => {
        entryEvidenceRef.current = recordKeystroke(entryEvidenceRef.current, Date.now());
    };

    // Read the evidence for a finished entry and start a fresh one. Returns { method, evidence } so
    // the caller can record both the verdict and what it was derived from — a label with no
    // evidence behind it is exactly what made the old `source` field impossible to argue with.
    const finishEntryEvidence = (value) => {
        const evidence = evidenceFor(entryEvidenceRef.current, value);
        entryEvidenceRef.current = emptyEntryEvidence();
        return { method: classifyEntryMethod(evidence), evidence };
    };

    useEffect(() => {
        assetsRef.current = assets;
    }, [assets]);

    useEffect(() => {
        formRef.current = {
            assetId: newAssetId,
            assetTypeId: newAssetAssetType,
            assetTag: newAssetAssetTag,
            serialNumber: newAssetSerialNumber,
            description: newAssetDescription,
            notes: newAssetNotes
        };
    }, [newAssetId, newAssetAssetType, newAssetAssetTag, newAssetSerialNumber,
        newAssetDescription, newAssetNotes]);

    // Registered ONCE. Everything it needs is behind a ref, so it never goes stale and
    // never has to be torn down and rebuilt between keystrokes.
    //
    // The two rules this handler applies -- which targets it should ignore, and how the buffer
    // accumulates -- live in utilities/scanInput.js, both because they are the parts that were
    // wrong and because they are the parts worth unit-testing.
    //
    // A text-entry element still owns its own keystrokes -- see scanInput.js for why the listener
    // must not also buffer them. What CHANGED 08/11/2026 is that focus no longer gets left
    // somewhere useless (see focusSerialInput below), so the case where a scan was typed into
    // Notes or the order picker and silently lost is addressed at its source.
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!shouldHandleScanKey(e.target)) {
                return;
            }

            if (isScanCharacter(e)) {
                const now = Date.now();
                scanBufferRef.current = appendScanKey(
                    scanBufferRef.current, e.key, now, lastKeyTimeRef.current);
                lastKeyTimeRef.current = now;
                // P1-18: same evidence as the field route, so both are classified by the same rule
                // rather than by which handler happened to run.
                noteKeystroke();
            }

            if (e.key === 'Enter') {
                const scanned = scanBufferRef.current;
                scanBufferRef.current = '';
                lastKeyTimeRef.current = 0;

                if (scanned.length > 0) {
                    if (isCompleteBarcode(scanned)) {
                        // Show it, then decide. The decision reads the value passed in,
                        // not this state, so the two cannot disagree.
                        setNewAssetSerialNumber(scanned);
                        runScanEntryRef.current({
                            serial: scanned, assetTag: '', trigger: 'document',
                            ...finishEntryEvidence(scanned)
                        });
                    } else {
                        // Previously `setError(...)` into state nothing rendered, so a
                        // short read was completely silent -- the operator saw a scan
                        // that simply did nothing.
                        setSnackbar({ status: 'warning', message: 'Barcode too short — please scan again.' });
                        // Discard this read's evidence, or its keystrokes would be attributed to
                        // whatever the operator enters next.
                        entryEvidenceRef.current = emptyEntryEvidence();
                    }
                }
                e.preventDefault(); // Prevent form submission if in a form
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const collectionInProgress = selectedCollection == null || selectedCollection.end_time == null || selectedCollection.end_time === '' ;
    const selectedCollectionId = selectedCollection == null ? '' : selectedCollection.id;


    const handleAutoSave = () =>{
        if(newAssetId){
            editAsset();
        }
    }

    const editAsset = async () => {
        const currentAsset = assets.find(a => a.id === newAssetId);
        //console.log(currentAsset)
        const newAsset ={
            id: newAssetId,
            "collection_id": selectedCollectionId,
            "collector_id": auth.id,
            "asset_type_id": newAssetAssetType,
            "asset_tag": newAssetAssetTag,
            "serial_number": newAssetSerialNumber,
            "description": newAssetDescription,
            "collection_notes": newAssetNotes,
            "pending": true,
            "collected": currentAsset.collected,
            "legacy": false,
            "make_id":currentAsset.make_id,
            "manufacturer": currentAsset.manufacturer,
            "model": currentAsset.model,
            "model_id": currentAsset.model_id,
        };

        const result = await editAssetOnDb(auth.token, newAsset);

        if( result.status === "success") {
            setSnackbar(result);
            dispatch(updateAsset(newAsset));
            // P1-17: this path cleared nothing at all, so after an edit the form stayed
            // populated with the row just saved and the next entry had to be typed over it.
            completeEntry();
        } else {
            // P1-3: a failed edit is a refusal like any other, so it gets the same visible
            // treatment rather than a snackbar that may already have been replaced.
            //
            // This path carries NO scan identity (it sends the row's current `collected`),
            // so the server classifies it as an edit and it can never be refused as a
            // duplicate -- that is what keeps P1-1's notes-on-a-collected-asset fix working.
            refuseEntry({
                title: 'Update Failed',
                message: result?.message || 'Oh Snap! Update Failed!'
            });
        }
    }

    // P1-3: needs NO duplicate check of its own. A scan whose serial is already collected
    // on this order -- including one that was itself added on site as unexpected -- is
    // classified DUPLICATE by runScanEntry and never reaches here. The old code had no
    // check at all on this path, which is how a re-scan created a second physical row.
    //
    // `decision` is passed through only so a server-side rejection can be attributed to the
    // same scan; the rest of the payload comes from the form, because for an unexpected item
    // the form IS the record.
    //
    // ⚠ `scannedSerial` must be passed on the scan paths. A gun scan calls
    // setNewAssetSerialNumber() and then classifies SYNCHRONOUSLY, so reading
    // `newAssetSerialNumber` here would get the PREVIOUS entry's value -- the same
    // same-tick staleness that broke the scan buffer. It used to be safe only because the
    // old flow detoured through an effect, which forced a render in between. The "Add
    // Unexpected Asset" button has no scan to pass and falls back to the form.
    const saveNewAsset = async (collected=false, decision=null, entry=null, scannedSerial=null,
                                entryValues=null) => {
       const enteredSerial = String(scannedSerial ?? newAssetSerialNumber ?? '');
       // A scan supplies its values explicitly (decided in runScanEntry); the "Add Unexpected
       // Asset" button has no scan, so the form IS the record and is read directly.
       const values = entryValues ?? {
           assetTypeId: newAssetAssetType,
           assetTag: newAssetAssetTag,
           description: newAssetDescription,
           notes: newAssetNotes,
           fromEditor: true
       };

       if(!values.assetTypeId ) {
            // P1-17: this is the likeliest cause of the reported symptom. pinAssetType
            // defaults on while pinnedAssetType starts empty, so unless the operator has
            // used the dropdown, every clear writes '' back into the type -- and the next
            // manually typed entry for an item that isn't on the intent list lands here and
            // returned before ever reaching the clear below, leaving the serial in place.
            //
            // Still worth saying out loud: an operator who has never touched the dropdown will
            // have every UNEXPECTED scan refused here. That is correct (asset type is a required
            // field per the requirements) but it is the single most likely thing to look like
            // "the scan didn't work", so the message names the field.
            refuseEntry({title: 'Asset Type Required', message: 'Oh Snap! Please select an Asset Type.'});
            return false;
        }else if(!enteredSerial.trim()){
            refuseEntry({title: 'Serial Number Required', message: 'Oh Snap! Please provide a Serial Number.'});
            return false;
        }else{
            const newAsset =         {
                "collection_id": selectedCollectionRef.current?.id ?? selectedCollectionId,
                "collector_id": auth.id,
                "asset_type_id": (values.assetTypeId > 0 ? values.assetTypeId : 10),
                "asset_tag": values.assetTag,
                "serial_number": enteredSerial.trim(),
                "description": values.description,
                "collection_notes": values.notes,
                "pending": true,
                "collected": collected,
                "legacy": false,
                // P1-12: collected_timestamp used to be built here as
                // toISOString().slice(...) -- a UTC reading written into a
                // timestamp-without-time-zone column, i.e. the wrong hour. The server
                // stamps it now. What the device contributes is the scan's identity,
                // and only when something was actually scanned.
                ...(collected ? takeScanIdentity() : {}),
            };

            const result = await createAsset(auth.token, newAsset);

            // P1-3: the server's own per-order guard caught something this device could
            // not see -- another operator collected or imported the serial in between.
            if (result?.code === 'DUPLICATE_SCAN') {
                registerDuplicateRejection(
                    { ...(decision || {}), serialKey: serialKey(newAsset.serial_number),
                      tagKey: '', duplicateOfId: result.duplicateOfId ?? null,
                      message: result.message },
                    entry, { fromServer: true });
                return false;
            }

            // The serial IS on this order's list, just not collected -- so this device's
            // asset list was stale and the scan should have been a match, not a new row.
            // Refreshed and refused rather than quietly collected: silently turning an
            // "unexpected" decision into a "match" would hide a real client-side problem.
            if (result?.code === 'SERIAL_ON_ORDER') {
                await refreshCollection();
                refuseEntry({
                    title: 'Already On This Order',
                    message: result.message || 'That serial is on this order\'s list.',
                    detail: 'The list has been refreshed — please scan it again.'
                });
                return false;
            }

            if( result.status === "success") {
                // P1-17: clear the form and the serial filter BEFORE the new row reaches the
                // asset list -- this ordering is the actual bug behind the report.
                //
                // The filter effect repopulates the editor whenever exactly one asset matches
                // the active serial filter. Before the save that filter matched nothing, so
                // typing was safe; the moment the newly created row lands in `assets` it
                // matches, and the effect writes its serial straight back into the field. The
                // clear ran, then was immediately undone. Clearing the filter first removes
                // the window entirely.
                completeEntry();

                // The created row as the server stored it -- including item_number, scan_number
                // and the unexpected flag it assigned, which the local object does not have.
                const created = Object.assign({}, newAsset, result.data || {}, {id: result.id});
                dispatch(addAsset(created));
                // Same change as collectMatchedAsset: no second full-collection GET per entry.
                // The guard is released sooner, so the next scan is not dropped.
                setStats(prior => applyAssetTransition(prior, null, created));
                // Match what collectAsset already does. This path saved silently -- no
                // confirmation and no float-to-top -- so adding an unexpected asset read as
                // "nothing happened" even when it had worked.
                setDataFilter({top: result.id});
                setSnackbar({status: 'success', 'message': (collected
                    ? 'Well Done! Unexpected asset successfully collected.'
                    : 'Well Done! Unexpected asset successfully created.')});

            }else{
                refuseEntry({
                    title: 'Save Failed',
                    message: result?.message || 'Oh Snap! Error saving asset.'
                });
            }

        }
    }

    const isCollecting = useRef(false);

    // P1-17 introduced this guard so two fast Enters could not double-submit.
    //
    // P1-3 generalises it to wrap the whole scan entry (classification included) and --
    // importantly -- makes it AUDIBLE. It used to `return` silently, so an item scanned
    // while a slow save was still in flight was dropped with no cue at all: no row, no
    // message, and if that scan was a duplicate, no count either. On a warehouse
    // connection that is a lost asset, not a rare edge case.
    const guardedRun = (run) => {
        if (isCollecting.current) {
            setSnackbar({
                status: 'warning',
                message: 'Still saving the previous scan — please scan that item again.'
            });
            return;
        }
        isCollecting.current = true;
        Promise.resolve()
            .then(run)
            .finally(() => { isCollecting.current = false; });
    };

    // Keeps the once-registered document listener calling the CURRENT closure rather than
    // the first render's, without re-attaching it on every keystroke (which is what made
    // the old buffer non-deterministic in the first place).
    useEffect(() => {
        runScanEntryRef.current = (args) => guardedRun(() => runScanEntry(args));
    });

    // P1-3: `collect` is gone. This used to fire a collect as a side effect of the filter
    // effect writing form fields, which is how the decision ended up depending on render
    // timing and selection state. Setting the editor fields now only ever sets fields.
    const setAssetEditorFields = (id, type, number, tag, description, notes) => {
        setNewAssetId(id);
        setNewAssetAssetType(type);
        setNewAssetSerialNumber(number);
        setNewAssetAssetTag(tag);
        setNewAssetDescription(description);
        setNewAssetNotes(notes);
    }

    // P1-15: reads the pin state directly rather than taking it as arguments.
    // The old signature (pinNotes, pinDescription, pinAssetType) shadowed the outer
    // pin state, so the seven call sites that passed nothing silently cleared
    // everything, and onClick={emptyNewAssetFields} handed React's MouseEvent in as
    // the first flag. pinNotes was accepted but never read at all, which is why the
    // Notes "Keep Value" checkbox did nothing.
    const emptyNewAssetFields = () => {
        setAssetEditorFields(
            null,
            pinAssetType ? pinnedAssetType : '',
            '',
            '',
            pinDescription ? newAssetDescription : '',
            pinNotes ? newAssetNotes : ''
        );
        setSelectionModel([]);
        setDataFilter([])
    }

    // ── Keeping focus where scanning works (08/11/2026) ─────────────────────────────
    //
    // The Serial # field is the intended home for the cursor while scanning, and it is now put
    // there deliberately: when an order is opened, and after every entry, accepted or refused.
    //
    // Two things this fixes. A scan aimed at the Notes field, the Asset Tag field or the order
    // Autocomplete used to be typed into that box while its terminating Enter did nothing --
    // silently, no row and no message. The Autocomplete was the common case because it keeps
    // focus after you pick an order, so a driver's very first scan could vanish. And it makes the
    // field's own Enter handler the normal route, which is the sturdier of the two: the DOM
    // accumulates the barcode there, so it cannot be truncated by a render stall the way the
    // document-listener buffer can.
    //
    // P1-12's comment warned against focusing this field, because it reroutes scans onto the
    // typed path. That was a hazard only while the two paths behaved differently; they no longer
    // do, so it is now the preferred path rather than an accident.
    const focusSerialInput = ({ select = false } = {}) => {
        const input = serialInputRef.current;
        if (input == null || typeof input.focus !== 'function') {
            return;
        }
        input.focus();
        if (select && typeof input.select === 'function') {
            input.select();
        }
    };

    // P1-17: every entry now ends in exactly one of these two, so what gets cleared no
    // longer depends on which of the five routes the operator happened to take (scan,
    // Enter with a match, Enter without one, Collect, Add Unexpected Asset, Edit Asset).
    // Reported by CompuCycle as "the serial stays in the field when typed manually", but
    // the underlying issue was that clearing was copy-pasted into some success branches
    // and absent from others -- Edit Asset never cleared at all, and no rejection path
    // cleared or signalled anything.

    // Accepted: blank the form and put the cursor back in Serial # for the next scan.
    //
    // ⚠ CHANGED 08/11/2026. This used to leave focus alone, on the reasoning that focusing the
    // field reroutes barcodes onto the typed code path. It did -- and that was the problem, not
    // the solution: whether focus happened to be there decided which of two DIFFERENT code paths
    // ran, so behaviour changed in runs. The two are now equivalent, and the typed route is the
    // sturdier of them, so putting the cursor here is deliberate.
    const completeEntry = () => {
        emptyNewAssetFields();
        // The duplicate highlight and its notice describe the previous, refused entry.
        // Once one is accepted they are stale, so a driver can keep scanning without
        // having to dismiss anything.
        setFlashDuplicateId(null);
        setBlockingNotice(null);
        focusSerialInput();
    };

    // Refused: keep what was entered so a typo stays visible and correctable, but select
    // it so the next keystroke -- or the next scan -- replaces it with nothing to delete.
    const rejectEntry = () => {
        focusSerialInput({ select: true });
    };

    // P1-3: the one way an entry is refused. Every rejection path goes through this, so a
    // refusal cannot be silent -- which is what the 03/06/2026 test call actually hit:
    // a duplicate that notified in one browser and not in another.
    //
    // Three channels, deliberately: a notice that does NOT auto-hide (the snackbar's
    // single slot and 6s timeout are why a refusal could be missed), a distinct tone for
    // an operator looking at the pallet rather than the screen, and the P1-17 behaviour of
    // keeping the serial selected so the next scan overwrites it with nothing to delete.
    const refuseEntry = ({ title, message, detail = null }) => {
        setBlockingNotice({ title, message, detail });
        playRejectTone();
        rejectEntry();
        // The grid keeps whatever filter the refusal set (candidate rows for an ambiguous
        // scan, the collided row for a duplicate); it is cleared by the next accepted entry.
    };


    const top100Films = collectionsWithRelations.map(c => ({
        label: `${c.lot_number} - ${c.customer.customer_name} - ${(c.start_time || "").slice(0, 10)}`,
        id: c.id
    }));


    return (
        <>
            <Box sx={{ height: 'auto', width: '100%' }}>
                <div style={{marginTop: 10,display:'flex', width:'100%', justifyContent:'space-between', marginBottom:15}}>
                    <div>
                        <Autocomplete
                            style={{marginRight: 10, width: 450}}
                            disablePortal
                            label="Collections"
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            options={top100Films}
                            getOptionLabel={(option) => option.label}
                            sx={{ width: 300 }}
                            renderInput={(params) => <TextField {...params} />}
                            onChange={(event, value) => {
                                // ⚠ Blur first (08/11/2026). This Autocomplete is a text input and it
                                // KEEPS FOCUS after an order is picked, so the driver's next keystrokes
                                // -- their first barcode -- went into this box, and its Enter does
                                // nothing. The scan disappeared with no row, no message and no tone.
                                // The load effect focuses Serial # once the assets arrive; this makes
                                // sure nothing is holding focus in the meantime.
                                event?.target?.blur?.();
                                if(value===null){
                                    emptyNewAssetFields();
                                    setOverrideId(0);
                                    setSelectedCollection(collectionsWithRelations.find( cwr => cwr.id === 0));
                                }else{
                                    let val = value?.id
                                    emptyNewAssetFields();
                                    setOverrideId(val);
                                    setSelectedCollection(collectionsWithRelations.find( cwr => cwr.id === val))
                                }
                            }}
                        />
                    </div>


                    {selectedCollection != null  && selectedCollection.scan_complete === false? (
                    <div>
                        <Button
                            tabIndex={-1}
                            id={'scan_complete_btn'}
                            onClick={(e) => setConfirm({
                                'open': true,
                                'action': () => scanCompleteAction(selectedCollection),
                                'title': 'Confirm',
                                'msg': 'Are you sure you want to complete scanning of this collection?',
                                'btn_msg': 'Yes! Complete Scanning'
                            })}
                            style={{marginLeft: 'auto'}}
                            variant="contained"
                            color="error"
                            disabled={false}
                        >
                            Scan Complete
                        </Button>

                    </div>) : null

                    }
                </div>

                {selectedCollection != null ? (
                    <>
                        <div id="the-collection" className="d-flex">
                            {/* P1-8: five tiles now, so the split is flexible rather than 60/40 —
                                five fixed tiles did not fit a 1366-wide driver laptop. */}
                            <div className="lefty" style={{flex: '1 1 auto', minWidth: 0}}>
                                <div className="d-flex" style={{'gap':30}}>
                                    <div>
                                        <h2>ORDER# {selectedCollection.lot_number}
                                            {/* P1-5: the driver has to be able to see which mode
                                                they are in without inferring it from which tiles
                                                are missing. Only shown for blind, because
                                                interactive is the default and badging it would
                                                make the normal case noisier for no information. */}
                                            {isBlind(selectedCollection) ? (
                                                <span className="blind-badge"
                                                      title="No intent list. Every scan is recorded; nothing is matched or flagged unexpected.">
                                                    BLIND SCAN
                                                </span>
                                            ) : null}
                                        </h2>

                                        <div className="date">
                                            {selectedCollection.scan_complete === true ? (
                                                <>
                                                   {selectedCollection.start_time?.slice(0, 10)} - {selectedCollection.scan_complete_date?.slice(0, 10)}
                                                    <span>SCAN COMPLETED</span>
                                                </>
                                            ) : (
                                                <>
                                                 {selectedCollection.start_time?.slice(0, 10)} - {collectionInProgress ? 'In Progress' : selectedCollection.end_time?.slice(0, 10)}
                                                </>
                                            )}
                                        </div>


                                    </div>
                                    <div className="col-4" style={{marginLeft: 80}}>
                                        <h3 style={{marginTop: 0}}>{selectedCollection.customer.customer_name || ''}</h3>
                                        <address>
                                            {selectedCollection.customer.address1 || ''}<br/>
                                            {(selectedCollection.customer.address2 != null && selectedCollection.customer.address2.trim() !== '') ?
                                                <>{`${selectedCollection.customer.address2}`}<br/></> : null}
                                            {formatCityStateZip(selectedCollection.customer.city, selectedCollection.state?.code, selectedCollection.customer.zip_code)}<br/>
                                        </address>
                                    </div>
                                </div>
                            </div>
                            <div className="righty" style={{flex: '0 0 auto', position: 'relative'}}>


                                {/* P1-5: which counts mean anything depends on the mode, and
                                    utilities/scanMode.js owns that so this header and the one in
                                    Collection Management cannot drift. A blind order has no intent
                                    list, so expected/matched/unscanned are structurally zero and
                                    nothing in it can be "unexpected" — four zeros would read as a
                                    broken screen, not as four empty categories. */}
                                <div className="stat-tiles">
                                    {showsStatTile(modeOf(selectedCollection), 'expected') ? (
                                    <div className={`boxed-content-stats ${dataFilter.value==='expected'?'active' : ''}`}
                                         onClick={() => handleDataFilter({hasFilter: true, filterType: 'stat', value: 'expected'})}>
                                        <h2>{stats.expected}</h2>
                                        <p><strong>Expected</strong> <br/>Asset Count</p>
                                    </div>
                                    ) : null}
                                    {showsStatTile(modeOf(selectedCollection), 'matched') ? (
                                    <div className={`boxed-content-stats ${dataFilter.value==='matched'?'active' : ''}`}
                                         onClick={() => handleDataFilter({hasFilter: true, filterType: 'stat', value: 'matched'})}>
                                        <h2>{stats.matched}</h2>
                                        <p><strong>Matched</strong> <br/> Asset Count</p>
                                    </div>
                                    ) : null}

                                    {showsStatTile(modeOf(selectedCollection), 'unscanned') ? (
                                    <div className={`boxed-content-stats ${dataFilter.value==='unscanned'?'active' : ''}`}
                                         onClick={() => handleDataFilter({hasFilter: true, filterType: 'stat', value: 'unscanned'})}>
                                        <h2>{stats.unscanned}</h2>
                                        <p><strong>Unscanned</strong> <br/>Asset Count</p>
                                    </div>
                                    ) : null}

                                    {showsStatTile(modeOf(selectedCollection), 'unexpected') ? (
                                    <div className={`boxed-content-stats ${dataFilter.value==='unexpected'?'active' : ''}`}
                                         onClick={() => handleDataFilter({hasFilter: true, filterType: 'stat', value:'unexpected'})}>
                                        <h2>{stats.unexpected} </h2>
                                        <p><strong>Unexpected</strong> <br/>Asset Count</p>
                                    </div>
                                    ) : null}

                                    {/* P1-5, blind mode: spec §4.3's "total assets scanned". Clicking
                                        it filters the grid to the collected rows AND opens the
                                        per-type breakdown — unlike the Duplicate tile, this count
                                        describes rows that exist, so filtering to them is honest and
                                        the count and the visible rows agree. */}
                                    {showsStatTile(modeOf(selectedCollection), 'totalScanned') ? (
                                    <div className={`boxed-content-stats ${dataFilter.value==='totalScanned'?'active' : ''}`}
                                         title="Everything scanned onto this order. Click for the breakdown by asset type."
                                         onClick={() => {
                                             handleDataFilter({hasFilter: true, filterType: 'stat', value: 'totalScanned'});
                                             setShowTypeBreakdown(true);
                                         }}>
                                        <h2>{stats.totalScanned}</h2>
                                        <p><strong>Total</strong> <br/>Assets Scanned</p>
                                    </div>
                                    ) : null}

                                    {/* P1-8: the fourth count the requirements ask for (spec §2.1).
                                        Deliberately NOT sourced from `stats`, which is the server's
                                        canonical shape and describes rows that exist — a duplicate is a
                                        refused scan and is persisted nowhere. See utilities/duplicateTally.js.

                                        Clicking it opens a log, and deliberately does NOT filter the grid:
                                        three attempts on one row would show one row against a count of
                                        three, which is exactly the tile/row disagreement P1-2 fixed.

                                        P1-18: `assets` is passed so the count only includes refusals whose
                                        cause still holds. CompuCycle reported the number staying at 1 after
                                        they deleted the offending item; it now drops when that row is
                                        deleted or un-collected, because at that point a re-scan would
                                        succeed and the warning is stale. */}
                                    <div className="boxed-content-stats duplicate"
                                         title="Duplicate scans refused during this session. Clears when the item that caused it is deleted or removed from the collection."
                                         onClick={() => {
                                             if (duplicateCount(duplicateTally, selectedCollection.id, assets) > 0) {
                                                 setShowDuplicateLog(true);
                                             }
                                         }}>
                                        <h2>{duplicateCount(duplicateTally, selectedCollection.id, assets)}</h2>
                                        <p><strong>Duplicate</strong> <br/>Scans Refused</p>
                                    </div>
                                </div>
                            </div>
                        </div>




                        <div style={{display: "flex", flexDirection: "column", padding: 20, marginTop: 1, backgroundColor: '#F3F4F6'}}>


                            <div style={{display: "flex", flexDirection: "row"}}>

                                {/* P1-15: green while the type is sticky, so the state is visible. */}
                                <div style={{display: "flex", flexDirection: "column", marginLeft: 5, paddingLeft: 5, paddingRight: 5, backgroundColor: pinAssetType ? "#d6ffe5" : "white"}}>
                                    <TextField
                                        style={{marginTop: 10, width: 200, backgroundColor:'white'}}
                                        id="asset-type-select"
                                        value={newAssetAssetType || ''}
                                        label="Asset Type"
                                        select
                                        onChange={(e) => handleNewAssetAssetTypeSelected(e.target.value)}

                                    >
                                        {/* P1-9: retired types are excluded from NEW entry but still
                                            resolve to a name everywhere else (the grid, the report),
                                            because `Unknown` holds 1,220 historical assets and those
                                            rows would otherwise render blank. `assetTypes` arrives
                                            ordered by sort_order now — it never was before. */}
                                        {assetTypes.filter(at => at.retiredAt == null)
                                            .map(at => <MenuItem key={at.id} value={at.id}>{at.name}</MenuItem>)}
                                    </TextField>

                                    {/* Inverted: sticky is the default, so this is an opt-out. */}
                                    <FormControlLabel style={{marginLeft: 0}} control={<Checkbox sx={{color: "#006225"}} checked={!pinAssetType}/>} label={"Clear after each scan"} onChange={(event) => setPinAssetType(!pinAssetType)}/>
                                </div>

                                <div>
                                    {/* inputRef is P1-17: rejectEntry() selects this field's text so a
                                        refused entry can be overwritten without deleting it first. */}
                                    <TextField onChange={(e) => handleNewAssetSerialNumber(e.target.value)}
                                               onPaste={() => {
                                                   // P1-18: direct evidence, and it outranks the timing rule.
                                                   // A paste is not "fast" — it is instantaneous, with NO
                                                   // character keydowns at all — so a timing-only heuristic
                                                   // would happily report it as a gun scan.
                                                   entryEvidenceRef.current = recordPaste(entryEvidenceRef.current);
                                               }}
                                               onKeyDown={(e) => {
                                                   if (e.key === 'Enter'){
                                                       e.preventDefault();
                                                       // P1-3: read e.target.value, NOT newAssetSerialNumber.
                                                       // When a gun scans into this focused field, the final
                                                       // character's onChange can batch with this keydown, so
                                                       // the state value can still be missing that character --
                                                       // the same staleness that broke the scan buffer. The DOM
                                                       // node always has the complete value.
                                                       // assetTag is passed explicitly and empty, exactly as
                                                       // the gun path does, so both routes classify on the
                                                       // serial alone and cannot disagree. Reading it from
                                                       // the form here let a tag left over from a previous
                                                       // item hijack the match.
                                                       runScanEntryRef.current({
                                                           serial: e.target.value, assetTag: '', trigger: 'field',
                                                           ...finishEntryEvidence(e.target.value)
                                                       });
                                                   } else if (isScanCharacter(e)) {
                                                       // P1-18: the gun's keystrokes land here when this field has
                                                       // focus, which after the 08/11 fix is the normal case. Same
                                                       // evidence, same classifier as the document route.
                                                       noteKeystroke();
                                                   }
                                               }}

                                               value={newAssetSerialNumber}
                                               inputRef={serialInputRef}

                                               style={{marginLeft: 10, marginTop: 10, width: 200, backgroundColor:'white'}}
                                               id="new-asset-serial-number" label="Serial #"
                                               variant="outlined" />
                                </div>

                                <div>
                                    {/* Enter submits, rather than doing nothing (08/11/2026). This field
                                        had no key handling at all, so a scan aimed at it was typed in here
                                        and its Enter was swallowed. It now runs the same entry point, on
                                        the Serial # value -- which for a mis-aimed scan is empty, so the
                                        operator gets a clear "Nothing To Scan" instead of silence. */}
                                    <TextField onChange={(e) => handleNewAssetTag(e.target.value)}
                                               onKeyDown={(e) => {
                                                   if (e.key === 'Enter') {
                                                       e.preventDefault();
                                                       runScanEntryRef.current({
                                                           serial: newAssetSerialNumber,
                                                           assetTag: e.target.value,
                                                           trigger: 'asset-tag-field',
                                                           ...finishEntryEvidence(newAssetSerialNumber)
                                                       });
                                                   }
                                               }}
                                               style={{marginLeft: 10, marginTop: 10, width: 200, backgroundColor:'white'}} id="new-asset-asset-tag" label="Asset Tag" variant="outlined" value={newAssetAssetTag}/>
                                </div>

                                {/*
                            <div style={{ display: "flex", flexDirection: "column", flex: 1, marginLeft:5, paddingLeft: 5, paddingRight:5, backgroundColor: pinDescription ? "#d6ffe5" : "white" }}>
                                <TextField onChange={(e) => setNewAssetDescription(e.target.value)} style={{flex: 1, marginTop: 10, backgroundColor: "white"}} id="new-asset-description" label="Description" variant="outlined" value={newAssetDescription} />
                                <FormControlLabel style={{marginLeft: 0}} control={<Checkbox sx={{color: "#006225"}} checked={pinDescription}  />} label={"Keep Value"} onChange={(event) => setPinDescription(!pinDescription)}/>
                            </div>
                             */}

                                {/* P1-15: the Notes pin now actually persists the value, so show it too.
                                    The onKeyDown is 08/11/2026: as with Asset Tag, Enter used to be
                                    swallowed here, so a scan aimed at Notes vanished with no message. */}
                                <div style={{display: "flex", flexDirection: "column", flex: 1, marginLeft: 5, paddingLeft: 5, paddingRight: 5, backgroundColor: pinNotes ? "#d6ffe5" : "white"}}>
                                    <TextField
                                        onChange={(e) => {
                                            setNewAssetNotes(e.target.value);
                                            //handleAutoSave();
                                        }}

                                        onBlur={(e)=>handleAutoSave()}

                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                runScanEntryRef.current({
                                                    serial: newAssetSerialNumber,
                                                    assetTag: newAssetAssetTag,
                                                    trigger: 'notes-field',
                                                    ...finishEntryEvidence(newAssetSerialNumber)
                                                });
                                            }
                                        }}

                                        style={{flex: 1, marginLeft: 10, marginTop: 10, backgroundColor:"white"}}
                                        id="new-asset-notes"
                                        label="Notes"
                                        variant="outlined"
                                        value={newAssetNotes}/>

                                    <FormControlLabel style={{marginLeft: 0}} control={<Checkbox sx={{color: "#006225"}} checked={pinNotes}/>} label={"Keep Value"} onChange={(event) => setPinNotes(!pinNotes)}/>
                                </div>

                            </div>
                            <div id="action_btns">

                                {selectedCollection.scan_complete === false ? (
                                    <>
                                        {/* P1-3: the Collect leg now runs the SAME entry point the gun and
                                            the typed Enter use, so all three cannot diverge -- including the
                                            duplicate check, which this button never performed at all. */}
                                        <Button
                                            onClick={assets.find(a => a.id === newAssetId)?.collected ? uncollectAssetConfirm :
                                                () => runScanEntryRef.current({
                                                    serial: newAssetSerialNumber,
                                                    assetTag: newAssetAssetTag,
                                                    trigger: 'collect-button',
                                                    // The button only decides WHEN to save. How the serial
                                                    // arrived is whatever the evidence says -- it may well have
                                                    // been scanned, then collected with a click.
                                                    ...finishEntryEvidence(newAssetSerialNumber)
                                                })
                                            }
                                            variant="contained"
                                            color={assets.find(a => a.id === newAssetId)?.collected ? 'warning' : 'primary'}
                                            /*
                                            disabled={!assets.some(asset => asset.serial_number === newAssetSerialNumber || asset.asset_tag === newAssetAssetTag)}

                                             */
                                        >
                                            {assets.find(a => a.id === newAssetId)?.collected ? 'Remove From Collection' : 'Collect'}
                                        </Button>

                                        {/* P1-5: "Add Unexpected Asset" has no meaning in a blind
                                            order — there is no list for an asset to be unexpected
                                            against — and it is redundant there: Collect runs the
                                            same runScanEntry path a gun scan does, which creates
                                            the row AND collects it. This button creates an
                                            UNCOLLECTED row, which in a blind job would show up as
                                            a count blind mode is meant to have dropped.

                                            This is also the control P1-6 role-gates. Removing it
                                            from blind mode first is the whole reason P1-5 had to
                                            precede P1-6: it was the only onsite workflow for a job
                                            with no intent list, and now it isn't.

                                            Edit Asset is unaffected — a selected row is editable in
                                            either mode. */}
                                        {/* P1-6: onsite drivers must not add unexpected assets to
                                            the intent list (CompuCycle, 07/29/2026). Manager+ only.

                                            ⚠ This element is DUAL-PURPOSE — it is "Edit Asset" once
                                            a row is selected — so the role only gates the add path.
                                            `newAssetId != null` short-circuits first, which keeps
                                            editing available to every role in both modes, exactly
                                            as before. A Collector on an interactive order now sees
                                            no button until they select a row.

                                            The server refuses this independently
                                            (routes/assets.js), on the payload shape rather than on
                                            the route, so that scanning — which posts to the same
                                            endpoint — keeps working for Collectors. */}
                                        {(newAssetId != null
                                          || (!isBlind(selectedCollection) && canAddUnexpectedAsset(userRole))) ? (
                                        <Button
                                            onClick={newAssetId == null
                                                ? () => saveNewAsset(false)
                                                : () => editAsset()
                                            }
                                            variant="contained"
                                            color="primary"
                                            disabled={false}
                                        >
                                            {newAssetId == null ? "Add Unexpected Asset" : "Edit Asset"}
                                        </Button>
                                        ) : null}

                                    </>
                                ) : (
                                    <>
                                        <Button
                                            onClick={editAsset}
                                            variant="contained"
                                            color="primary"
                                            disabled={newAssetId <= 0}>
                                            Edit Asset
                                        </Button>
                                    </>
                                )}

                                <Button
                                    tabIndex={-1}
                                    onClick={() => emptyNewAssetFields()}
                                    variant="contained"
                                    color="primary"
                                    disabled={false}
                                >Cancel Scan
                                </Button>


                            </div>
                        </div>
                    </>
                ) : null}

                <StripedDataGrid
                    slots={{toolbar: GridToolbar}}
                    getRowClassName={(params) => {
                        const classes = [];
                        if (params.row.collected){
                            classes.push("collected");
                        } else {
                            classes.push("not-collected");
                        }

                        if( params.indexRelativeToCurrentPage % 2 === 0  ) {
                            classes.push("even");
                        } else {
                            classes.push("odd");
                        }

                        // Spec §2.2 requires an unexpected item's row to be highlighted YELLOW.
                        // It never was: adding an unexpected asset always collects it too, so
                        // `collected` won and the row rendered green like any matched item. The
                        // CSS gives this rule higher specificity than .collected.
                        if (params.row.unexpected === true) {
                            classes.push("unexpected");
                        }

                        // P1-3: the row a duplicate scan just collided with, so "already
                        // collected" is something the operator can see and not just be told.
                        // Compared as strings: this id can come from the redux row (a number)
                        // or from the server's duplicateOfId, and a type mismatch here would
                        // silently mean no highlight at all.
                        //
                        // `activeFlashId`, not `flashDuplicateId`: the highlight is derived from
                        // whether a refusal against that row still stands, so clearing the
                        // duplicate log ends it immediately (2026-08-21).
                        if (activeFlashId != null && String(params.row.id) === String(activeFlashId)) {
                            classes.push("duplicate-flash");
                        }

                        return classes.join(' ')
                     }
                    }
                    onRowSelectionModelChange={handleSelection}
                    rowSelectionModel={selectionModel}
                    //rows={assetsWithRelations}
                    rows={(() => {
                        if(dataFilter.top && dataFilter.top > 0){
                            const items = [...assetsWithRelations];
                            const itemToMove = items.find(item => item.id === dataFilter.top);
                            if (itemToMove) {
                                items.splice(items.indexOf(itemToMove), 1);
                                items.unshift(itemToMove);
                            }
                            return items;
                        }else{
                           return assetsWithRelations;
                        }
                    })()}

                    columns={columns}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 20,
                            },
                        },
                    }}
                    density="compact"
                    autoHeight={true}
                    fullWidth={true}
                    pageSizeOptions={pageSizeOptions}
                />
                {!isLoaded ? (
                    <div style={{position: "fixed", top:0, bottom: 0, left: 0, right:0, backgroundColor: "#00000022", display: "flex", justifyContent: "center", alignItems: "center"}}>
                        <div className="lds-roller">
                            <div></div>
                            <div></div>
                            <div></div>
                            <div></div>
                            <div></div>
                            <div></div>
                            <div></div>
                            <div></div>
                        </div>
                    </div>
                ) : null}
                {/* P1-3: the refusal notice.
                    This markup was already here, gated on `moreThanOneMatchMessage` — state that
                    was only ever set to `false`, so it had never once rendered. It is now the
                    single channel for every refused entry: duplicate, ambiguous, validation and
                    server error.

                    A blocking notice rather than the snackbar, because the snackbar auto-hides
                    after 6s and has one slot, so a following message replaces it. A duplicate the
                    operator never saw is precisely the 03/06/2026 symptom.

                    The OK button is deliberately NOT autoFocused: focusing it would route the
                    scan gun's keystrokes into a button instead of the document listener. The
                    notice also clears itself on the next accepted entry (see completeEntry), so a
                    driver can keep scanning without stopping to dismiss it. */}
                {blockingNotice ? (
                    <div style={{position: "fixed", top:0, bottom: 0, left: 0, right:0, backgroundColor: "#00000055", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1400}}>
                        <div style={{backgroundColor: "white", padding: 25, borderRadius: 5, display: "flex", flexDirection: "column", alignItems: "center", maxWidth: 520, borderTop: '6px solid #c62828'}}>
                            <div style={{fontWeight: 'bold', fontSize: 22, marginBottom: 10, color: '#c62828'}}>
                                {blockingNotice.title}
                            </div>
                            <div style={{marginBottom: blockingNotice.detail ? 6 : 20, textAlign: 'center'}}>
                                {blockingNotice.message}
                            </div>
                            {blockingNotice.detail ? (
                                <div style={{marginBottom: 20, textAlign: 'center', color: '#555', fontSize: 14}}>
                                    {blockingNotice.detail}
                                </div>
                            ) : null}
                            <Button onClick={() => setBlockingNotice(null)} style={{marginLeft: 10, marginTop:10, marginBottom: 10, width: 200, height: 50}} variant="contained" color="primary">OK</Button>
                        </div>
                    </div>
                ) : null}

                {/* P1-8: what the Duplicate tile counted. A list, not a grid filter — repeat
                    attempts on one row would make the tile's number disagree with the row count.

                    P1-18: the "Source" column is now "Entry" and reports how the characters actually
                    ARRIVED — scanned / typed / pasted, or an honest "unknown" — rather than which
                    code path ran. The old column said `typed` for a barcode scanned into the focused
                    Serial # field, which is what CompuCycle reported. Both the list and the count
                    are filtered by `assets`, so they always agree. */}
                {showDuplicateLog ? (
                    <Dialog open={true} onClose={() => setShowDuplicateLog(false)} maxWidth="sm" fullWidth={true}>
                        <DialogTitle>Duplicate scans refused this session</DialogTitle>
                        <DialogContent>
                            <DialogContentText style={{marginBottom: 10}}>
                                These scans were rejected because the item was already collected on this
                                order. None of them changed any count. An entry stops being listed once the
                                item that caused it is deleted or removed from the collection.
                            </DialogContentText>
                            <table style={{width: '100%', borderCollapse: 'collapse'}}>
                                <thead>
                                    <tr style={{textAlign: 'left', borderBottom: '1px solid #ddd'}}>
                                        <th style={{padding: 6}}>Serial</th>
                                        <th style={{padding: 6}}>Item #</th>
                                        <th style={{padding: 6}}>Time</th>
                                        <th style={{padding: 6}}>Entry</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {duplicateEvents(duplicateTally, selectedCollection?.id, assets).map((e, i) => (
                                        <tr key={`${e.at}-${i}`} style={{borderBottom: '1px solid #f0f0f0'}}>
                                            <td style={{padding: 6}}>{e.serial}</td>
                                            <td style={{padding: 6}}>{itemNumbers.get(e.assetId) ?? '—'}</td>
                                            <td style={{padding: 6}}>{e.at ? new Date(e.at).toLocaleTimeString() : ''}</td>
                                            {/* The evidence behind the verdict is in the tooltip, so a
                                                surprising label can be argued with rather than just
                                                disbelieved. */}
                                            <td style={{padding: 6}}
                                                title={e.evidence
                                                    ? `${e.evidence.keystrokes} keystroke(s)`
                                                      + `${e.evidence.avgGapMs != null ? `, ~${Math.round(e.evidence.avgGapMs)} ms apart` : ''}`
                                                      + `${e.evidence.pasted ? ', paste detected' : ''}`
                                                      + `${e.trigger ? ` (${e.trigger})` : ''}`
                                                    : ''}>
                                                {describeEntryMethod(e.method)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {hiddenDuplicateCount(duplicateTally, selectedCollection?.id, assets) > 0 ? (
                                <DialogContentText style={{marginTop: 10, fontSize: 13}}>
                                    …and {hiddenDuplicateCount(duplicateTally, selectedCollection?.id, assets)} more
                                    not shown.
                                </DialogContentText>
                            ) : null}
                        </DialogContent>
                        <DialogActions>
                            {/* P1-18: an explicit reset, so the operator is never stuck with a number
                                they can only clear by reloading the page. */}
                            <Button
                                onClick={() => {
                                    setDuplicateTally(tally => clearDuplicates(tally, selectedCollection?.id));
                                    setShowDuplicateLog(false);
                                }}
                                color="secondary">Clear</Button>
                            <Button onClick={() => setShowDuplicateLog(false)} variant="contained">Close</Button>
                        </DialogActions>
                    </Dialog>
                ) : null}

                {/* P1-5: the per-type breakdown spec §4.3 requires of both modes, on screen.
                    Built from statsByTypeName — the SAME helper CollectionReports.js uses for
                    the exported per-type block — so what the driver reads here and what the
                    customer reads in the report cannot be two different numbers.

                    includeZero: false, unlike the report. The report lists all fourteen types
                    because the requirements specify the totals against that list; a driver
                    mid-job wants to know what is in the truck, and ten zeroes is noise. */}
                {showTypeBreakdown ? (
                    <Dialog open={true} onClose={() => setShowTypeBreakdown(false)} maxWidth="xs" fullWidth={true}>
                        <DialogTitle>Scanned by asset type</DialogTitle>
                        <DialogContent>
                            <DialogContentText style={{marginBottom: 10}}>
                                {stats.totalScanned} asset{stats.totalScanned === 1 ? '' : 's'} scanned
                                onto this order.
                            </DialogContentText>
                            <table style={{width: '100%', borderCollapse: 'collapse'}}>
                                <tbody>
                                    {Object.entries(statsByTypeName(stats, assetTypes, {includeZero: false}))
                                        .map(([name, count]) => (
                                            <tr key={name} style={{borderBottom: '1px solid #f0f0f0'}}>
                                                <td style={{padding: 6}}>{name}</td>
                                                <td style={{padding: 6, textAlign: 'right'}}>{count}</td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                            {stats.totalScanned === 0 ? (
                                <DialogContentText style={{marginTop: 10, fontSize: 13}}>
                                    Nothing scanned yet.
                                </DialogContentText>
                            ) : null}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowTypeBreakdown(false)} variant="contained">Close</Button>
                        </DialogActions>
                    </Dialog>
                ) : null}


                {openSnackbar ?
                    <Snackbar  open={true} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} autoHideDuration={6000}>
                        <Alert
                            onClose={handleSnackbarClose}
                            severity={openSnackbar.status}
                            sx={{ width: '100%' }} >
                            {openSnackbar.message}
                        </Alert>
                    </Snackbar>
                    : null}


                <Dialog
                    open={confirm.open}
                    onClose={handleConfirmClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description">

                    <DialogTitle id="alert-dialog-title">
                        {confirm.title}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            {confirm.msg}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions style={{paddingBottom: 20, paddingRight: 20}}>
                        {/* P1-13: the label is overridable because this dialog is no longer
                            only used for "are you sure you want to destroy something" —
                            the scan-complete handoff asks "generate the report now?", where
                            "No! Cancel" reads as though declining undoes the completion.
                            Defaults to the original string, so every other caller is
                            unaffected. */}
                        <Button onClick={handleConfirmClose}>{confirm.cancel_msg || 'No! Cancel '}</Button>
                        <Button variant="contained" onClick={() => confirm.action()} color="primary" autoFocus>{confirm.btn_msg}</Button>
                    </DialogActions>
                </Dialog>


            </Box>

        </>
    )
}

export default AssetManagement;

